import { Cascader } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { useEffect, useState } from 'react';

type DataItem = {
  id: number;
  name: string;
  parentId?: number;
};

type Option = {
  value: number;
  label: string;
  disabled?: boolean;
  children?: Option[];
};

const buildChildrenOptions = <T extends DataItem>(
  dataSource: T[],
  options: T[],
  isOptionDisabled?: (item: T) => boolean
): Option[] =>
  options.map((option) => {
    const children = dataSource.filter((el) => el.parentId === option.id);

    if (!children.length) {
      return {
        value: option.id,
        label: option.name,
        disabled: isOptionDisabled?.(option),
      };
    }

    const childrenOptions = buildChildrenOptions(
      dataSource,
      children,
      isOptionDisabled
    );

    return {
      value: option.id,
      label: option.name,
      disabled: childrenOptions.every((child) => child.disabled),
      children: childrenOptions,
    };
  });

const mapDataSource = <T extends DataItem>(
  dataSource: T[],
  isOptionDisabled?: (item: T) => boolean
): Option[] => {
  const parentOptions = dataSource.filter((el) => el.parentId === undefined);
  const allOptions = buildChildrenOptions(
    dataSource,
    parentOptions,
    isOptionDisabled
  );

  return allOptions;
};

const findPath = (
  leafValue: number,
  options: Option[],
  valuePath: number[]
): number[] | null =>
  options.reduce<number[] | null>((acc, option) => {
    if (acc) {
      return acc;
    }

    const newPath = [...valuePath, option.value];

    if (option.value === leafValue) {
      return newPath;
    }

    if (option.children) {
      return findPath(leafValue, option.children, newPath);
    }

    return null;
  }, null);

const getPath = (value: number | undefined, options: Option[]): number[] => {
  if (value === undefined) {
    return [];
  }

  return findPath(value, options, []) ?? [];
};

export const Cascada = <T extends DataItem>({
  dataSource,
  value,
  onChange,
  mode,
  isOptionDisabled,
}: Props<T>) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [path, setPath] = useState<number[]>([]);

  useEffect(() => {
    const newOptions = mapDataSource(dataSource, isOptionDisabled);

    setOptions(newOptions);
  }, [dataSource, isOptionDisabled]);

  useEffect(() => {
    const newPath = getPath(value, options);

    setPath(newPath);
  }, [options, value]);

  if (!onChange) {
    return null;
  }

  const handleChange = (selectedPath: (string | number)[]) => {
    const newValue = selectedPath?.[selectedPath.length - 1];

    return onChange(newValue as number);
  };

  const filterFunction = (
    inputValue: string,
    inputPath: DefaultOptionType[]
  ) => {
    const formattedInput = inputValue.toLowerCase();

    return inputPath.some((option) => {
      const formattedLabel = (option.label as string).toLowerCase();

      return formattedLabel.includes(formattedInput);
    });
  };

  if (mode === 'view') {
    const viewValues = path.reduce((acc: string, val: number) => {
      const name = dataSource.find((opt) => opt.id === val)?.name;

      if (acc === '') {
        return `${name}`;
      }

      return `${acc} / ${name}`;
    }, '');

    return <div>{viewValues}</div>;
  }

  return (
    <Cascader<Option>
      placeholder="Select a category"
      value={path}
      onChange={handleChange}
      expandTrigger="hover"
      options={options}
      showSearch={{ filter: filterFunction }}
      getPopupContainer={(trigger) => trigger.parentElement || trigger}
    />
  );
};

type Props<T extends DataItem> = {
  dataSource: T[];
  value?: number;
  onChange?: (newValue: number) => void;
  mode: string;
  isOptionDisabled?: (item: T) => boolean;
};
